import { useState, useEffect, lazy, Suspense } from 'react';
import {
    ArrowLeft, ArrowRight, Database, Map,
    GitBranch, Rocket, Check, Loader2, Eye, Layers, ShieldAlert, AlertTriangle, Lock, Unlock, Save, ExternalLink, Globe, Users, Shield,
    UploadCloud, Zap, Clock
} from 'lucide-react';
import { useAuth } from '@/context/useAuth';
import { useNotification } from '@/context/NotificationContext';
import { apiGet, apiPost, apiPatch, interactiveFetch } from '../api';
const WorklistRunnerPage = lazy(() => import('./WorklistRunnerPage'));

const STEPS = [
    { id: 1, label: 'Source',     icon: Database },
    { id: 2, label: 'Schema',     icon: Map },
    { id: 3, label: 'Access',     icon: GitBranch },
    { id: 4, label: 'Write-back', icon: UploadCloud },
    { id: 5, label: 'Publish',    icon: Rocket },
];

// ── Stable module-level component — MUST be outside WorklistEditorPage ──────
// Defining this inside the render function causes React to treat it as a new
// component type on every render, unmounting the input after each keystroke.
function UserPillSelect({ label, hint, users, selectedIds, onAdd, onRemove, searchVal, onSearch, excludeIds = [] }) {
    const filtered = users.filter(u =>
        !selectedIds.includes(u.id) &&
        !excludeIds.includes(u.id) &&
        (u.name || u.email || '').toLowerCase().includes(searchVal.toLowerCase())
    );
    return (
        <Field label={label}>
            {hint && <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-2">{hint}</p>}
            <div className="min-h-[44px] flex flex-wrap gap-1.5 p-2.5 border border-gray-200 dark:border-slate-700 rounded-xl bg-gray-50/50 dark:bg-slate-800/40 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-500/10 transition-all">
                {selectedIds.map(id => {
                    const u = users.find(x => x.id === id);
                    return (
                        <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-semibold border border-indigo-200 dark:border-indigo-800/50">
                            <span className="w-4 h-4 rounded-full bg-indigo-500 text-white text-[9px] flex items-center justify-center font-bold shrink-0">
                                {(u?.name || u?.email || '?')[0].toUpperCase()}
                            </span>
                            {u?.name || u?.email || id}
                            <button type="button" onClick={() => onRemove(id)} className="ml-0.5 text-indigo-400 hover:text-red-500 transition-colors font-black text-[10px]">✕</button>
                        </span>
                    );
                })}
                <input
                    value={searchVal}
                    onChange={e => onSearch(e.target.value)}
                    placeholder={users.length === 0 ? 'Loading users…' : selectedIds.length === 0 ? 'Type to search users…' : 'Add more…'}
                    className="flex-1 min-w-[140px] bg-transparent text-xs text-gray-700 dark:text-slate-300 outline-none placeholder:text-gray-400"
                />
            </div>
            {searchVal && filtered.length > 0 && (
                <div className="mt-1 border border-gray-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 shadow-lg max-h-44 overflow-y-auto">
                    {filtered.slice(0, 10).map(u => (
                        <button key={u.id} type="button"
                            onMouseDown={e => { e.preventDefault(); onAdd(u.id); onSearch(''); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-indigo-50 dark:hover:bg-slate-800 text-left transition-colors">
                            <span className="w-6 h-6 rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                                {(u.name || u.email || '?')[0].toUpperCase()}
                            </span>
                            <div>
                                <p className="text-xs font-semibold text-gray-800 dark:text-slate-200">{u.name || u.email}</p>
                                {u.name && <p className="text-[10px] text-gray-400 dark:text-slate-500">{u.email}</p>}
                            </div>
                        </button>
                    ))}
                </div>
            )}
            {searchVal && filtered.length === 0 && (
                <p className="mt-1 text-[11px] text-gray-400 px-1">
                    {users.length === 0 ? '⚠ Users not loaded — check permissions' : 'No matching users found'}
                </p>
            )}
        </Field>
    );
}

export default function WorklistEditorPage({ appId, onBack, onSaved, onDraftSaved }) {
    const isEdit = !!appId;
    const { token, user } = useAuth();
    const { addNotification } = useNotification();

    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);  // Step 1 data-preview loading
    const [errors, setErrors] = useState({});
    const [showConfirm, setShowConfirm] = useState(false);
    const [showPreview, setShowPreview] = useState(false); // Step 4 full-screen preview modal
    const [savedId, setSavedId] = useState(appId || null);
    const [previewRows, setPreviewRows] = useState([]);
    const [datasources, setDatasources] = useState([]);

    // Form state
    const [dataModels, setDataModels] = useState([]);
    const [dmTables, setDmTables] = useState([]); // tables of selected data model
    const [flows, setFlows] = useState([]);
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const visibleRoles = roles.filter(r => (user?.roleId === 'r1' || user?.roleId === 'r2') ? true : r.scope === 'Tenant');
    const [activePermissionMenu, setActivePermissionMenu] = useState(null);
    const [userSearchInclude, setUserSearchInclude] = useState('');
    const [userSearchExclude, setUserSearchExclude] = useState('');
    const [userSearchAssignee, setUserSearchAssignee] = useState('');

    const [form, setForm] = useState({
        name: '', description: '',
        source_type: 'data_model',          // default Phase 1: Data Model
        datasource_id: '', source_query: '', source_table: '',
        data_model_id: '', data_model_table: '',
        sharepoint_config: { site_hostname: '', site_path: '', drive_name: 'Documents', workbook_path: '', workbook_file: '', worksheet_name: 'Sheet1', tenant_id: '', client_id: '', client_secret: '' },
        schema_map: [],
        template_id: 'classic_table',
        workflow_config: { statuses: ['Open', 'In Progress', 'Pending Review', 'Closed'], transitions: {}, assignee_pool: [] },
        writeback_mode: 'realtime',
        writeback_schedule: '',
        flow_id: '',
        writeback_sink: { type: 'same_as_source', write_mode: 'update_rows', target_type: 'table' },
        writeback_trigger: 'bulk_admin',
        writeback_fields: null,
        folder_name: '', shared_with: ['*'],
        include_users: [],                   // specific user IDs granted access
        exclude_users: [],                   // specific user IDs denied access
        record_visibility: 'all',            // 'all' | 'assigned'
        assignee_pool: [],                   // user IDs who can be assigned items
        default_assignee: '',                // user ID or '' for None
        allow_add_entry: 'all',              // 'all' | 'admin' | 'none' — who can add new records
        default_sort_col: '',                // field key to sort by default
        default_sort_dir: 'asc',             // 'asc' | 'desc'
    });

    useEffect(() => {
        apiGet('/datasources', token).then(d => setDatasources(Array.isArray(d) ? d : [])).catch(() => addNotification('Failed to load datasources', 'error'));
        apiGet('/datamodels/', token).then(d => setDataModels(Array.isArray(d) ? d : [])).catch(() => addNotification('Failed to load data models', 'error'));
        apiGet('/flows', token).then(d => setFlows(Array.isArray(d) ? d : [])).catch(() => addNotification('Failed to load flows', 'error'));
        apiGet('/roles', token).then(r => setRoles(Array.isArray(r) ? r : [])).catch(() => addNotification('Failed to load roles', 'error'));
        apiGet('/users/', token).then(u => setUsers(Array.isArray(u) ? u : [])).catch(() => {});

        if (isEdit) {
            apiGet(`/worklists/${appId}`, token).then(async d => {
                if (d) {
                    setForm(prev => ({
                        ...prev, ...d,
                        sharepoint_config: d.sharepoint_config || prev.sharepoint_config,
                        schema_map: Array.isArray(d.schema_map) ? d.schema_map : [],
                        workflow_config: d.workflow_config || prev.workflow_config,
                    }));
                    if (d.source_type === 'data_model' && d.data_model_id) {
                        const dm = await apiGet(`/datamodels/${d.data_model_id}`, token).catch(() => null);
                        if (Array.isArray(dm?.tables)) setDmTables(dm.tables.map(t => t.name).filter(Boolean));
                        if (d.data_model_table) {
                            try {
                                setPreviewing(true);
                                const sql = `SELECT * FROM "${d.data_model_table}" LIMIT 10`;
                                const res = await interactiveFetch(`/datamodels/${d.data_model_id}/query`, token, {
                                    method: 'POST',
                                    body: JSON.stringify({ sql })
                                });
                                const rows = res?.rows || [];
                                setPreviewRows(Array.isArray(rows) ? rows : []);
                            } catch (err) {
                                console.error("Edit mode preview load failed:", err);
                            } finally {
                                setPreviewing(false);
                            }
                        }
                    }
                    if (d.datasource_id && (d.source_query || d.source_table)) {
                        try {
                            setPreviewing(true);
                            const rows = await apiPost('/worklists/preview-source', token, {
                                datasource_id: d.datasource_id, source_query: d.source_query || null,
                                source_table: d.source_table || null, limit: 10,
                            });
                            setPreviewRows(Array.isArray(rows) ? rows : []);
                        } catch { }
                        finally { setPreviewing(false); }
                    }
                }
            }).catch(() => addNotification('Failed to load worklist config', 'error'));
        }
    }, [token]);

    // When data model changes, load its tables
    const handleDataModelChange = async (modelId) => {
        patch('data_model_id', modelId);
        patch('data_model_table', '');
        setDmTables([]);
        if (!modelId) return;
        // DatabaseOut.tables is a flat list of TableSchema objects with a 'name' field
        const dm = await apiGet(`/datamodels/${modelId}`, token).catch(() => null);
        if (Array.isArray(dm?.tables)) {
            setDmTables(dm.tables.map(t => t.name).filter(Boolean));
        }
    };

    const handleGridRowChange = (index, key, value) => {
        setForm(f => {
            const next = [...(f.schema_map || [])];
            next[index] = { ...next[index], [key]: value };
            return { ...f, schema_map: next };
        });
    };

    // Auto-detect status options when a column is marked as status
    useEffect(() => {
        const statusRow = form.schema_map?.find(r => r.is_status_column);
        if (statusRow && previewRows.length > 0) {
            const distinct = [...new Set(previewRows.map(r => r[statusRow.source_field]).filter(Boolean))];
            if (distinct.length > 0 && distinct.length <= 20) {
                if (!form.workflow_config?.statuses?.length || form.workflow_config.statuses.join(',') !== distinct.join(',')) {
                     patchWf('statuses', distinct);
                }
            }
        }
    }, [form.schema_map, previewRows]);

    // Smart 3-way schema sync: triggered whenever preview data arrives
    // Rules (product-grade):
    //   1. Strip internal __ keys (pk, sp_row_index) — they are not real source columns
    //   2. Preserve ALL existing config (core_mapping, field_type, options, etc.) for known columns
    //   3. New columns from source → appended as unconfirmed rows
    //   4. Columns that were previously saved but are NO LONGER in the source → marked with [Removed] suffix
    //      so the admin can consciously decide what to do, nothing is silently dropped
    useEffect(() => {
        if (previewRows.length > 0) {
            const sourceCols = Object.keys(previewRows[0]).filter(c => !c.startsWith('__'));
            setForm(f => {
                const existingMap = f.schema_map || [];
                const existingFieldSet = new Set(existingMap.map(r => r.source_field));
                const sourceColSet = new Set(sourceCols);

                // Add brand-new columns (not in saved map at all)
                const newRows = sourceCols
                    .filter(c => !existingFieldSet.has(c))
                    .map(c => ({
                        is_selected: true,
                        source_field: c,
                        core_mapping: 'None',
                        is_read_only: false,
                        field_type: 'As-Is',
                        options: '',
                        is_status_column: false,
                        is_remarks_column: false,
                        update_flag: 'NA',
                    }));

                // Mark stale columns (were saved but no longer in source)
                const updatedExisting = existingMap.map(r => {
                    const wasRemoved = !sourceColSet.has(r.source_field) && !r.source_field?.endsWith(' [Removed]');
                    if (wasRemoved) {
                        return { ...r, is_selected: false, _stale: true, source_field: `${r.source_field} [Removed]` };
                    }
                    return r;
                });

                const merged = [...updatedExisting, ...newRows];
                // Only update if something actually changed
                if (newRows.length > 0 || updatedExisting.some(r => r._stale)) {
                    return { ...f, schema_map: merged };
                }
                return f;
            });
        }
    }, [previewRows]);

    const patch = (key, val) => setForm(f => ({ ...f, [key]: val }));
    const patchWf = (key, val) => setForm(f => ({
        ...f, workflow_config: { ...f.workflow_config, [key]: val }
    }));
    const patchSp = (key, val) => setForm(f => ({
        ...f, sharepoint_config: { ...f.sharepoint_config, [key]: val }
    }));

    const handlePreview = async () => {
        setPreviewing(true);
        try {
            let rows;
            if (form.source_type === 'data_model') {
                if (!form.data_model_id || !form.data_model_table) {
                    addNotification('Select a Data Model and a table first', 'warning');
                    return;
                }
                 const sql = `SELECT * FROM "${form.data_model_table}" LIMIT 10`;
                 const res = await interactiveFetch(`/datamodels/${form.data_model_id}/query`, token, {
                     method: 'POST',
                     body: JSON.stringify({ sql })
                 });
                 rows = res?.rows || [];
             } else {
                if (!form.datasource_id || (!form.source_query && !form.source_table)) {
                    addNotification('Select a datasource and provide a query or table name', 'warning');
                    return;
                }
                const res = await interactiveFetch('/datasources/query', token, {
                    method: 'POST',
                    body: JSON.stringify({
                        datasource_id: form.datasource_id,
                        source_query: form.source_query || null,
                        source_table: form.source_table || null,
                        limit: 10,
                    })
                });
                rows = res?.rows || [];
            }
            setPreviewRows(Array.isArray(rows) ? rows : []);
            if (!rows?.length) addNotification('Query returned no rows', 'warning');
        } catch (e) {
            addNotification('Preview failed: ' + (e?.message || ''), 'error');
        } finally {
            setPreviewing(false);
        }
    };


    // ── Step Validation ──────────────────────────────────────────────────────
    const validateStep = (s) => {
        const e = {};
        if (s === 1) {
            if (!form.name.trim()) e.name = 'Worklist name is required';
            if (form.source_type === 'data_model' && !form.data_model_id) e.source = 'Please select a data model';
            if (form.source_type === 'sql' && !form.datasource_id) e.source = 'Please select a datasource';
        }
        if (s === 2) {
            if (form.schema_map?.length > 0 && !form.schema_map?.find(r => r.core_mapping === 'Primary Key' && r.is_selected)) {
                e.pk = 'Primary Key mapping is required';
            } else if (!form.schema_map || form.schema_map.length === 0) {
                e.pk = 'Please go back to Step 1 and preview your data to load columns.';
            }
        }
        if (s === 3) {
            if (!form.workflow_config?.statuses?.length) e.statuses = 'At least one status is required';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleNext = () => {
        if (validateStep(step)) {
            setStep(s => s + 1);
        } else {
            addNotification('Please fix the highlighted errors before continuing', 'warning');
        }
    };

    const handleSave = async () => {
        if (!validateStep(4)) {
            addNotification('Please review your configuration', 'warning');
            return;
        }
        setShowConfirm(false);
        setSaving(true);
        try {
            const payload = { ...form };
            if (payload.source_type === 'sharepoint' && payload.writeback_mode === 'sql_table') {
                payload.writeback_mode = 'platform_managed';
            }

            if (isEdit) {
                await apiPatch(`/worklists/${appId}`, token, payload);
                addNotification('Worklist updated', 'success');
                if (onBack) onBack();
            } else {
                const res = await apiPost('/worklists', token, payload);
                addNotification('Worklist created!', 'success');
                if (onSaved) onSaved(res.id);
            }
        } catch (err) {
            addNotification(`Save failed: ${err?.message || 'Unknown error'}`, 'error');
        } finally {
            setSaving(false);
        }
    };



    const colsFromPreview = previewRows.length > 0 ? Object.keys(previewRows[0]).filter(c => !c.startsWith('__')) : [];

    // ── Permission Guard ─────────────────────────────────────────────────────
    const canManageWorklists = user?.roleId === 'r1' || user?.roleId === 'r2' || user?.roleId === 'r4';
    if (!canManageWorklists) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-slate-950 gap-4">
                <div className="p-4 rounded-full bg-red-50 dark:bg-red-900/20">
                    <ShieldAlert className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">Access Denied</h2>
                <p className="text-sm text-gray-500 dark:text-slate-400 text-center max-w-sm">
                    You don't have permission to create or edit worklists. Please contact your administrator.
                </p>
                <button onClick={onBack} className="mt-2 px-4 py-2 rounded-xl bg-gray-200 dark:bg-slate-800 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-300 dark:hover:bg-slate-700 transition-colors">
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <>
        <div className="flex flex-col h-full bg-gray-50 dark:bg-slate-950">
            {/* Confirmation Dialog */}
            {showConfirm && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-4 border border-gray-100 dark:border-slate-800">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-900/30">
                                <AlertTriangle className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100">{isEdit ? 'Save Changes?' : 'Publish Worklist?'}</h3>
                                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">This will make the worklist available to assigned roles.</p>
                            </div>
                        </div>
                        <div className="p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl space-y-1.5">
                            <p className="text-xs text-gray-600 dark:text-slate-300"><span className="font-semibold">Name:</span> {form.name}</p>
                            <p className="text-xs text-gray-600 dark:text-slate-300"><span className="font-semibold">Source:</span> {form.source_type}</p>
                            <p className="text-xs text-gray-600 dark:text-slate-300"><span className="font-semibold">Template:</span> Classic Worklist</p>
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                            <button onClick={() => setShowConfirm(false)} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
                                Cancel
                            </button>
                            <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                                {isEdit ? 'Save' : 'Publish'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex-none px-6 py-4 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 flex items-center gap-3">
                <button onClick={onBack} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500 transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                </button>
                <div>
                    <h1 className="text-lg font-bold text-gray-900 dark:text-slate-100">{isEdit ? 'Edit Worklist' : 'New Worklist'}</h1>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Configure your worklist in {STEPS.length} steps</p>
                </div>
            </div>

            {/* Step bar */}
            <div className="flex-none px-6 py-5.5 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-150 dark:border-slate-800/80">
                <div className="max-w-xl mx-auto flex flex-col gap-2">

                    {/* Circles row — line lives here so it's always centred behind the circles */}
                    <div className="relative flex items-center justify-between">
                        {/* Background Progress Line */}
                        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[3px] bg-gray-100 dark:bg-slate-800/60 z-0 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-emerald-500 transition-all duration-500 ease-out"
                                style={{ width: `${((step - 1) / (STEPS.length - 1)) * 100}%` }}
                            />
                        </div>

                        {STEPS.map((s) => {
                            const Icon = s.icon;
                            const done = step > s.id;
                            const active = step === s.id;
                            return (
                                <button
                                    key={s.id}
                                    onClick={() => done && setStep(s.id)}
                                    disabled={!done}
                                    className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 hover:scale-102 active:scale-98 ${
                                        active ? 'bg-gradient-to-br from-indigo-550 to-indigo-600 text-white shadow-[0_0_20px_rgba(99,102,241,0.4)] ring-4 ring-indigo-500/20 scale-110' :
                                        done  ? 'bg-emerald-500 text-white cursor-pointer hover:bg-emerald-600 shadow-[0_0_15px_rgba(16,185,129,0.2)]' :
                                        'bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm border border-gray-200 dark:border-slate-700/80 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    {active && (
                                        <span className="absolute inset-0 rounded-full bg-indigo-500/20 animate-ping" />
                                    )}
                                    {done ? <Check className="w-4 h-4 font-bold" /> : <Icon className="w-4 h-4" />}
                                </button>
                            );
                        })}
                    </div>

                    {/* Labels row — separate row below circles */}
                    <div className="flex items-start justify-between">
                        {STEPS.map((s) => {
                            const done = step > s.id;
                            const active = step === s.id;
                            return (
                                <span key={s.id} className={`w-10 text-center text-[10px] tracking-widest font-extrabold uppercase transition-colors ${
                                    active ? 'text-indigo-600 dark:text-indigo-400' :
                                    done  ? 'text-emerald-600 dark:text-emerald-500' :
                                    'text-gray-400'
                                }`}>
                                    {s.label}
                                </span>
                            );
                        })}
                    </div>

                </div>
            </div>

            {/* Step content */}
            <div className={`flex-1 overflow-y-auto p-6 mx-auto w-full transition-all duration-500 ease-in-out ${step === 2 ? 'max-w-7xl' : 'max-w-3xl'}`}>
                {step === 1 && (
                    <StepCard title="Data Source" desc="Where does your worklist data come from?">
                        <Field label="Worklist Name *">
                            <input className={`${input} ${errors.name ? 'border-red-400 dark:border-red-500 ring-2 ring-red-500/10' : ''}`} placeholder="e.g. AR Claims Review" value={form.name} onChange={e => { patch('name', e.target.value); if (errors.name) setErrors(prev => ({ ...prev, name: undefined })); }} />
                            {errors.name && <p className="text-[10px] text-red-500 font-semibold mt-1">{errors.name}</p>}
                        </Field>
                        <Field label="Description">
                            <textarea className={`${input} h-20 resize-none`} placeholder="What is this worklist for?" value={form.description} onChange={e => patch('description', e.target.value)} />
                        </Field>
                        <Field label="Source Type">
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { v: 'data_model', l: 'Data Model', icon: <Layers className="w-4 h-4" /> },
                                    { v: 'sql', l: 'SQL / Database', icon: <Database className="w-4 h-4" /> },
                                    { v: 'sharepoint', l: 'SharePoint Excel', icon: <span className="text-[10px] font-extrabold uppercase font-sans tracking-wide">SP</span> },
                                ].map(({ v, l, icon }) => (
                                    <button key={v} type="button" onClick={() => {
                                        patch('source_type', v);
                                        if (v === 'sharepoint') {
                                            patch('writeback_mode', 'sharepoint_adapter');
                                        } else if (v !== 'sql') {
                                            patch('writeback_mode', 'platform_managed');
                                        }
                                    }}
                                        className={`flex flex-col items-center gap-2 py-4 rounded-2xl border-2 text-xs font-bold transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.02] active:scale-[0.98] ${form.source_type === v
                                                ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 shadow-[0_0_20px_rgba(99,102,241,0.12)]'
                                                : 'border-gray-200 dark:border-slate-800/80 text-gray-500 dark:text-slate-400 hover:border-indigo-350 dark:hover:border-indigo-800/80 bg-white/40 dark:bg-slate-900/40'
                                            }`}>
                                        <div className={`p-2.5 rounded-xl transition-all ${form.source_type === v ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/25' : 'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500'}`}>
                                            {icon}
                                        </div>
                                        <span>{l}</span>
                                    </button>
                                ))}
                            </div>
                        </Field>

                        {/* ── Data Model source ── */}
                        {form.source_type === 'data_model' && (<>
                            <Field label="Data Model *">
                                <select className={input} value={form.data_model_id} onChange={e => handleDataModelChange(e.target.value)}>
                                    <option value="">— Select Data Model —</option>
                                    {dataModels.map(dm => <option key={dm.id} value={dm.id}>{dm.name}</option>)}
                                </select>
                            </Field>
                            {dmTables.length > 0 && (
                                <Field label="Table / View *">
                                    <select className={input} value={form.data_model_table} onChange={e => patch('data_model_table', e.target.value)}>
                                        <option value="">— Select table —</option>
                                        {dmTables.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </Field>
                            )}
                            {form.data_model_id && form.data_model_table && (
                                <button onClick={async () => {
                                    setPreviewing(true);
                                    try {
                                         const sql = `SELECT * FROM "${form.data_model_table}" LIMIT 10`;
                                         const res = await interactiveFetch(`/datamodels/${form.data_model_id}/query`, token, {
                                             method: 'POST',
                                             body: JSON.stringify({ sql })
                                         });
                                         const rows = res?.rows || [];
                                        setPreviewRows(Array.isArray(rows) ? rows : []);
                                        if (!rows.length) addNotification('No rows returned', 'warning');
                                    } catch (e) { addNotification('Preview failed: ' + (e?.message || ''), 'error'); }
                                    finally { setPreviewing(false); }
                                }} disabled={previewing}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-200 transition-colors">
                                    {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                    Preview Data Model
                                </button>
                            )}
                        </>)}

                        {/* ── SQL source ── */}
                        {form.source_type === 'sql' && (<>
                            <Field label="Datasource *">
                                <select className={input} value={form.datasource_id} onChange={e => patch('datasource_id', e.target.value)}>
                                    <option value="">— Select datasource —</option>
                                    {datasources.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </Field>
                            <Field label="SQL Query">
                                <textarea className={`${input} h-28 resize-none font-mono text-sm`} placeholder="SELECT * FROM dbo.ARClaims" value={form.source_query} onChange={e => patch('source_query', e.target.value)} />
                            </Field>
                            <Field label="— or — Table / View Name">
                                <input className={input} placeholder="dbo.ARClaims" value={form.source_table} onChange={e => patch('source_table', e.target.value)} />
                            </Field>
                            <button onClick={handlePreview} disabled={previewing}
                                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-200 transition-colors">
                                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                Preview Data
                            </button>
                        </>)}

                        {/* ── SharePoint Excel source ── */}
                        {form.source_type === 'sharepoint' && (<>
                            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">SharePoint Location</p>
                            <Field label="SharePoint Domain *">
                                <div className="flex items-center gap-2">
                                    <input className={input} placeholder="aidastech0" value={form.sharepoint_config.site_hostname} onChange={e => patchSp('site_hostname', e.target.value)} />
                                    <span className="text-sm text-gray-400 dark:text-slate-500 whitespace-nowrap">.sharepoint.com</span>
                                </div>
                            </Field>
                            <Field label="Site Name *">
                                <input className={input} placeholder="AIDASPlatform" value={form.sharepoint_config.site_path} onChange={e => patchSp('site_path', e.target.value)} />
                            </Field>
                            <Field label="SharePoint Folder Path">
                                <input className={input} placeholder="aidas_apps_demo/patients_charts" value={form.sharepoint_config.workbook_path} onChange={e => patchSp('workbook_path', e.target.value)} />
                                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Folder path INSIDE the document library (e.g., /GenAI/Internal). Do NOT include the library name.</p>
                            </Field>
                            <Field label="Workbook File Name *">
                                <input className={input} placeholder="ARClaims.xlsx" value={form.sharepoint_config.workbook_file} onChange={e => patchSp('workbook_file', e.target.value)} />
                            </Field>
                            <Field label="Worksheet Name">
                                <input className={input} placeholder="Sheet1" value={form.sharepoint_config.worksheet_name} onChange={e => patchSp('worksheet_name', e.target.value)} />
                            </Field>
                            <div className="border-t border-gray-200 dark:border-slate-700 pt-3 mt-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Azure AD App Registration</p>
                                    <button type="button" onClick={async () => {
                                        try {
                                            const res = await interactiveFetch('/datasources/sharepoint-creds', token, { method: 'GET' });
                                            if (res?.tenant_id) {
                                                patchSp('tenant_id', res.tenant_id || '');
                                                patchSp('client_id', res.client_id || '');
                                                patchSp('client_secret', res.client_secret || '');
                                                addNotification('Credentials loaded from integration', 'success');
                                            } else {
                                                addNotification('No SharePoint integration found. Enter credentials manually.', 'warning');
                                            }
                                        } catch { addNotification('Could not load integration keys', 'error'); }
                                    }} className="text-xs text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 font-medium">
                                        Load from Integration
                                    </button>
                                </div>
                            </div>
                            <Field label="Azure Tenant ID *">
                                <input className={input} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={form.sharepoint_config.tenant_id} onChange={e => patchSp('tenant_id', e.target.value)} />
                            </Field>
                            <Field label="Client ID *">
                                <input className={input} placeholder="App (client) ID" value={form.sharepoint_config.client_id} onChange={e => patchSp('client_id', e.target.value)} />
                            </Field>
                            <Field label="Client Secret *">
                                <input type="password" className={input} placeholder="Client secret value" value={form.sharepoint_config.client_secret} onChange={e => patchSp('client_secret', e.target.value)} />
                            </Field>
                            <button onClick={async () => {
                                const sp = form.sharepoint_config;
                                if (!sp.site_hostname || !sp.site_path) { addNotification('Enter SharePoint Domain and Site Name', 'warning'); return; }
                                if (!sp.workbook_file) { addNotification('Enter the workbook file name', 'warning'); return; }
                                if (!sp.tenant_id || !sp.client_id || !sp.client_secret) { addNotification('Enter Azure AD credentials', 'warning'); return; }
                                setPreviewing(true);
                                try {
                                    const fullPath = sp.workbook_path
                                        ? `${sp.workbook_path.replace(/\/$/,'')}/${sp.workbook_file}`
                                        : sp.workbook_file;
                                    const res = await interactiveFetch('/datasources/sharepoint-preview', token, {
                                        method: 'POST',
                                        body: JSON.stringify({
                                            sharepoint_domain: sp.site_hostname,
                                            site_name: sp.site_path,
                                            drive_name: sp.drive_name || 'Documents',
                                            workbook_path: fullPath,
                                            worksheet_name: sp.worksheet_name || 'Sheet1',
                                            azure_tenant_id: sp.tenant_id,
                                            client_id: sp.client_id,
                                            client_secret: sp.client_secret,
                                            limit: 10,
                                        })
                                    });
                                    const rows = res?.rows || [];
                                    setPreviewRows(Array.isArray(rows) ? rows : []);
                                    if (!rows.length) addNotification('No rows returned from workbook', 'warning');
                                } catch (e) { addNotification('SharePoint preview failed: ' + (e?.message || ''), 'error'); }
                                finally { setPreviewing(false); }
                            }} disabled={previewing}
                                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-200 transition-colors">
                                {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                                Preview SharePoint Data
                            </button>
                        </>)}

                        {previewRows.length > 0 && (
                            <div className="mt-4 space-y-2">
                                <label className="block text-[10px] tracking-wider font-extrabold uppercase text-gray-500">Live Connection Preview (First 5 Rows)</label>
                                <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-slate-800/80 bg-gray-50/30 dark:bg-slate-900/50 shadow-inner">
                                    <table className="text-xs w-full border-collapse">
                                        <thead>
                                            <tr className="bg-gray-100/80 dark:bg-slate-800/60 border-b border-gray-200 dark:border-slate-850">
                                                {Object.keys(previewRows[0]).map(c => (
                                                    <th key={c} className="px-4 py-2.5 text-left font-bold text-gray-500 dark:text-slate-400 tracking-wide">{c}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewRows.slice(0, 5).map((r, i) => (
                                                <tr key={i} className="border-b border-gray-100/50 dark:border-slate-800/50 hover:bg-gray-100/20 dark:hover:bg-slate-800/25 transition-colors">
                                                    {Object.values(r).map((v, j) => (
                                                        <td key={j} className="px-4 py-2.5 text-gray-700 dark:text-slate-300 font-mono text-[11px] truncate max-w-[140px]">{String(v ?? '')}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </StepCard>
                )}

                {step === 2 && (
    <StepCard title="Schema Mapping" desc="Select the columns to include, map them to system roles, and configure how each field behaves in the worklist UI.">
        {errors.pk && (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-400 font-medium">{errors.pk}</p>
            </div>
        )}
        {colsFromPreview.length === 0 && (!form.schema_map || form.schema_map.length === 0) && (
            <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl">
                <Eye className="w-5 h-5 text-amber-500 shrink-0" />
                <p className="text-sm text-amber-700 dark:text-amber-300">Go back to <strong>Step 1</strong> and preview your data first to load columns.</p>
            </div>
        )}

        {form.schema_map?.length > 0 && (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold text-gray-700 dark:text-slate-200">Schema Mapping Grid</p>
                        <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-0.5">{form.schema_map.filter(r => r.is_selected).length} of {form.schema_map.length} columns selected</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button type="button" onClick={() => {
                            const allSelected = form.schema_map.every(x => x.is_selected);
                            setForm(f => ({ ...f, schema_map: f.schema_map.map(r => ({ ...r, is_selected: !allSelected })) }));
                        }} className="text-xs text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 font-semibold underline underline-offset-2">
                            {form.schema_map.every(x => x.is_selected) ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>
                </div>
                
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
                    <table className="w-full text-xs text-left">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-slate-800/80 border-b border-gray-200 dark:border-slate-700">
                                <th className="px-3 py-3 font-semibold text-gray-500 dark:text-slate-400 w-10 text-center">
                                    <input type="checkbox"
                                        checked={form.schema_map.length > 0 && form.schema_map.every(r => r.is_selected)}
                                        onChange={(e) => {
                                            const val = e.target.checked;
                                            setForm(f => ({ ...f, schema_map: f.schema_map.map(r => ({ ...r, is_selected: val })) }));
                                        }}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                                    />
                                </th>
                                <th className="px-3 py-3 font-semibold text-gray-500 dark:text-slate-400 min-w-[130px]">Field Name</th>
                                <th className="px-3 py-3 font-semibold text-gray-500 dark:text-slate-400 min-w-[170px]">
                                    Core Mapping
                                    <span className="block text-[10px] font-normal text-gray-400 normal-case tracking-normal">System role for this field</span>
                                </th>
                                <th className="px-3 py-3 font-semibold text-gray-500 dark:text-slate-400 text-center min-w-[90px]">
                                    Read-Only
                                    <span className="block text-[10px] font-normal text-gray-400 normal-case tracking-normal">Users cannot edit</span>
                                </th>
                                <th className="px-3 py-3 font-semibold text-gray-500 dark:text-slate-400 min-w-[160px]">
                                    Field Type
                                    <span className="block text-[10px] font-normal text-gray-400 normal-case tracking-normal">UI control for users</span>
                                </th>
                                <th className="px-3 py-3 font-semibold text-gray-500 dark:text-slate-400 min-w-[180px]">
                                    Dropdown Options
                                    <span className="block text-[10px] font-normal text-gray-400 normal-case tracking-normal">Comma-separated values</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-800/60">
                            {form.schema_map.map((row, idx) => {
                                const isStale = row.source_field?.endsWith(' [Removed]');
                                return (
                                <tr key={row.source_field + idx} className={`transition-colors ${
                                    isStale ? 'bg-red-50/50 dark:bg-red-900/10' :
                                    !row.is_selected ? 'opacity-40' :
                                    'hover:bg-indigo-50/30 dark:hover:bg-slate-800/30'
                                }`}>
                                    <td className="px-3 py-2.5 text-center">
                                        <input type="checkbox"
                                            checked={row.is_selected}
                                            onChange={e => handleGridRowChange(idx, 'is_selected', e.target.checked)}
                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5"
                                        />
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className={`font-mono font-medium text-[11px] ${
                                            isStale ? 'text-red-500 line-through' :
                                            row.is_selected ? 'text-gray-800 dark:text-slate-200' : 'text-gray-400'
                                        }`}>{row.source_field}</span>
                                        {isStale && <span className="ml-1 text-[9px] text-red-500 font-semibold">Not in source</span>}
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <select
                                            value={row.core_mapping || 'None'}
                                            onChange={e => handleGridRowChange(idx, 'core_mapping', e.target.value)}
                                            disabled={!row.is_selected}
                                            className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-gray-700 dark:text-slate-300 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-400 outline-none disabled:opacity-40"
                                        >
                                            <optgroup label="— System Fields —">
                                                <option value="None">None</option>
                                                <option value="Primary Key">🔑 Primary Key</option>
                                            </optgroup>
                                            <optgroup label="— Display —">
                                                <option value="Title">📌 Title</option>
                                                <option value="Subtitle">📎 Subtitle</option>
                                                <option value="Description">📝 Description</option>
                                                <option value="Reference Number">🔢 Reference Number</option>
                                                <option value="Category">🗂 Category</option>
                                                <option value="Priority">⚡ Priority</option>
                                                <option value="Tags">🏷 Tags</option>
                                            </optgroup>
                                            <optgroup label="— Workflow —">
                                                <option value="Status">🔄 Status</option>
                                                <option value="Assignee">👤 Assignee</option>
                                                <option value="Reviewer">👁 Reviewer</option>
                                                <option value="Approver">✅ Approver</option>
                                                <option value="Due Date">📅 Due Date</option>
                                                <option value="Start Date">🗓 Start Date</option>
                                                <option value="Completed Date">✔ Completed Date</option>
                                                <option value="SLA Deadline">⏰ SLA Deadline</option>
                                                <option value="Escalation Flag">🚨 Escalation Flag</option>
                                            </optgroup>
                                            <optgroup label="— Financial —">
                                                <option value="Amount">💰 Amount</option>
                                                <option value="Currency">💱 Currency</option>
                                                <option value="Quantity">📦 Quantity</option>
                                                <option value="Percentage">% Percentage</option>
                                            </optgroup>
                                            <optgroup label="— Identity —">
                                                <option value="Patient ID">🆔 Patient ID</option>
                                                <option value="Patient Name">🧑 Patient Name</option>
                                                <option value="Provider">🏥 Provider</option>
                                                <option value="Facility">🏢 Facility</option>
                                                <option value="Department">🏛 Department</option>
                                            </optgroup>
                                            <optgroup label="— Audit —">
                                                <option value="Created At">🕐 Created At</option>
                                                <option value="Updated At">🕑 Updated At</option>
                                                <option value="Created By">✍ Created By</option>
                                                <option value="Remarks">💬 Remarks</option>
                                            </optgroup>
                                        </select>
                                    </td>
                                    <td className="px-3 py-2.5 text-center">
                                        <label className="inline-flex flex-col items-center gap-1 cursor-pointer">
                                            <div className={`relative w-8 h-4 rounded-full transition-colors ${
                                                row.is_read_only ? 'bg-amber-400' : 'bg-gray-200 dark:bg-slate-700'
                                            } ${!row.is_selected ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                onClick={() => row.is_selected && handleGridRowChange(idx, 'is_read_only', !row.is_read_only)}>
                                                <span className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform ${
                                                    row.is_read_only ? 'translate-x-4' : 'translate-x-0.5'
                                                }`} />
                                            </div>
                                            <span className={`text-[9px] font-semibold ${
                                                row.is_read_only ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'
                                            }`}>{row.is_read_only ? 'Read-Only' : 'Editable'}</span>
                                        </label>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <select
                                            value={row.field_type || 'As-Is'}
                                            onChange={e => handleGridRowChange(idx, 'field_type', e.target.value)}
                                            disabled={!row.is_selected}
                                            className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-gray-700 dark:text-slate-300 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-400 outline-none disabled:opacity-40"
                                        >
                                            <optgroup label="— Text —">
                                                <option value="As-Is">As-Is (Raw)</option>
                                                <option value="Text Box">Text Box</option>
                                                <option value="Text Area">Text Area (Multi-line)</option>
                                                <option value="Rich Text">Rich Text</option>
                                                <option value="URL">URL / Link</option>
                                                <option value="Email">Email</option>
                                                <option value="Phone">Phone Number</option>
                                            </optgroup>
                                            <optgroup label="— Numbers —">
                                                <option value="Whole Number">Whole Number</option>
                                                <option value="Decimal">Decimal</option>
                                                <option value="Currency">Currency</option>
                                                <option value="Percentage">Percentage (%)</option>
                                            </optgroup>
                                            <optgroup label="— Date / Time —">
                                                <option value="Date">Date</option>
                                                <option value="Date Time">Date + Time</option>
                                                <option value="Time">Time Only</option>
                                                <option value="Duration">Duration</option>
                                            </optgroup>
                                            <optgroup label="— Selection —">
                                                <option value="Drop-Down">Drop-Down (Single)</option>
                                                <option value="Multi-Select">Multi-Select</option>
                                                <option value="Radio">Radio Button</option>
                                                <option value="Checkbox">Checkbox (Boolean)</option>
                                                <option value="Toggle">Toggle</option>
                                            </optgroup>
                                            <optgroup label="— Lookup —">
                                                <option value="User Picker">User Picker</option>
                                                <option value="File Attachment">File Attachment</option>
                                            </optgroup>
                                        </select>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <input
                                            type="text"
                                            value={row.options || ''}
                                            onChange={e => handleGridRowChange(idx, 'options', e.target.value)}
                                            disabled={!row.is_selected || !['Drop-Down','Multi-Select','Radio'].includes(row.field_type)}
                                            placeholder={['Drop-Down','Multi-Select','Radio'].includes(row.field_type) ? 'Open, Closed, Pending' : '—'}
                                            className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs text-gray-700 dark:text-slate-300 focus:ring-1 focus:ring-indigo-500 outline-none disabled:opacity-20 placeholder:text-gray-300 dark:placeholder:text-slate-600"
                                        />
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        )}

        {/* Workflow Statuses — always shown, highlights when a Status column is mapped */}
        {(() => {
            const hasStatusCol = form.schema_map?.some(r => r.is_selected && r.core_mapping === 'Status');
            return (
            <div className="space-y-2 border-t border-gray-200 dark:border-slate-700 pt-4 mt-6">
                <Field label="Workflow Statuses">
                    <p className="text-[10px] mb-1.5">
                        {hasStatusCol
                            ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold">✓ Status column mapped — add or remove status values below</span>
                            : <span className="text-amber-500 font-semibold">⚠ Tip: map a column to <strong>Status</strong> in Core Mapping to drive these from your data</span>
                        }
                    </p>
                    <div className={`flex flex-wrap gap-2 p-3 rounded-2xl border ${errors.statuses ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-slate-700/80'} bg-gray-50/50 dark:bg-slate-800/30 min-h-[44px]`}>
                        {(form.workflow_config?.statuses || []).map((s, i) => (
                            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-bold border border-indigo-200 dark:border-indigo-800/50">
                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                {s}
                                <button type="button" onClick={() => {
                                    const next = (form.workflow_config?.statuses || []).filter((_, idx) => idx !== i);
                                    patchWf('statuses', next);
                                }} className="ml-0.5 text-indigo-400 hover:text-red-500 transition-colors text-[10px] font-black">✕</button>
                            </span>
                        ))}
                        <input
                            type="text"
                            placeholder="Type & press Enter to add a status…"
                            className="flex-1 min-w-[160px] bg-transparent text-xs text-gray-700 dark:text-slate-300 outline-none placeholder:text-gray-400"
                            onKeyDown={e => {
                                if (e.key === 'Enter' && e.target.value.trim()) {
                                    e.preventDefault();
                                    const val = e.target.value.trim();
                                    const current = form.workflow_config?.statuses || [];
                                    if (!current.includes(val)) {
                                        patchWf('statuses', [...current, val]);
                                    }
                                    e.target.value = '';
                                    if (errors.statuses) setErrors(prev => ({ ...prev, statuses: undefined }));
                                }
                            }}
                        />
                    </div>
                    {errors.statuses && <p className="text-[10px] text-red-500 font-semibold mt-1">{errors.statuses}</p>}
                </Field>
            </div>
            );
        })()}

            {/* ── New Record Entry config ────────────────────────────────────── */}
            <div className="space-y-3 border-t border-gray-200 dark:border-slate-700 pt-4 mt-4">
                <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                        <span className="text-[10px]">➕</span>
                    </div>
                    <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">New Record Entry</p>
                    <p className="text-[10px] text-gray-400 dark:text-slate-500 ml-auto">Who can add new rows to this worklist?</p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    {[
                        { v: 'all',   icon: '👥', label: 'All Users',    desc: 'Any user with access can add new records' },
                        { v: 'admin', icon: '🔐', label: 'Admins Only',  desc: 'Only admin roles can add new records' },
                        { v: 'none',  icon: '🚫', label: 'Disabled',     desc: 'No one can add new records via the form' },
                    ].map(opt => (
                        <button key={opt.v} type="button" onClick={() => patch('allow_add_entry', opt.v)}
                            className={`text-left p-3 rounded-2xl border-2 transition-all ${
                                (form.allow_add_entry || 'all') === opt.v
                                    ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20'
                                    : 'border-gray-200 dark:border-slate-700 hover:border-emerald-300 bg-white dark:bg-slate-900'
                            }`}>
                            <p className="text-lg mb-1">{opt.icon}</p>
                            <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{opt.label}</p>
                            <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 leading-relaxed">{opt.desc}</p>
                        </button>
                    ))}
                </div>
                {(form.allow_add_entry || 'all') !== 'none' && (
                    <div className="mt-2 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl">
                        <p className="text-[10px] text-gray-500 dark:text-slate-400 font-semibold">
                            ℹ️ The Add Entry form will show all <strong>editable</strong> fields defined above. Read-only fields are excluded automatically.
                        </p>
                    </div>
                )}
            </div>

    </StepCard>
)}


                {step === 3 && (
                    <StepCard title="Access & Write-back" desc="Configure who can access this worklist, what they can see, and how edits are written back.">

                        {/* ── SECTION: Role Access ── */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                <div className="w-5 h-5 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
                                    <span className="text-[10px]">👥</span>
                                </div>
                                <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Role Access</p>
                            </div>

                            <Field label="Roles with Access">
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-2">Select which roles can view and interact with this worklist. Leave all unselected to allow all roles.</p>
                                <div className="flex flex-wrap gap-2">
                                    {visibleRoles.map(r => {
                                        const isSelected = form.shared_with?.includes(r.id) && !form.shared_with?.includes('*');
                                        return (
                                            <button key={r.id} type="button" onClick={() => {
                                                let next = [...(form.shared_with || [])].filter(x => x !== '*');
                                                if (isSelected) {
                                                    next = next.filter(x => x !== r.id);
                                                } else {
                                                    next.push(r.id);
                                                }
                                                patch('shared_with', next.length === 0 ? ['*'] : next);
                                            }}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition-all ${
                                                isSelected
                                                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                                                    : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:border-indigo-300 dark:hover:border-indigo-800 bg-white dark:bg-slate-900'
                                            }`}>
                                                {isSelected && <Check className="w-3 h-3" />}
                                                {r.name || r.id}
                                            </button>
                                        );
                                    })}
                                    {visibleRoles.length === 0 && <p className="text-xs text-gray-400">No roles available</p>}
                                </div>
                                {form.shared_with?.includes('*') && (
                                    <p className="mt-2 text-[10px] text-amber-600 dark:text-amber-400 font-semibold">⚠ No roles selected — all users can access this worklist</p>
                                )}
                            </Field>

                            <UserPillSelect
                                label="Include Specific Users"
                                hint="Grant access to specific users regardless of their role."
                                users={users}
                                selectedIds={form.include_users || []}
                                onAdd={id => patch('include_users', [...(form.include_users || []), id])}
                                onRemove={id => patch('include_users', (form.include_users || []).filter(x => x !== id))}
                                searchVal={userSearchInclude}
                                onSearch={setUserSearchInclude}
                                excludeIds={form.exclude_users || []}
                            />

                            <UserPillSelect
                                label="Exclude Specific Users"
                                hint="Explicitly deny access to these users, even if they are in an allowed role."
                                users={users}
                                selectedIds={form.exclude_users || []}
                                onAdd={id => patch('exclude_users', [...(form.exclude_users || []), id])}
                                onRemove={id => patch('exclude_users', (form.exclude_users || []).filter(x => x !== id))}
                                searchVal={userSearchExclude}
                                onSearch={setUserSearchExclude}
                                excludeIds={form.include_users || []}
                            />
                        </div>

                        {/* ── SECTION: Record Visibility ── */}
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                <div className="w-5 h-5 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                                    <span className="text-[10px]">👁</span>
                                </div>
                                <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Record Visibility</p>
                            </div>
                            <Field label="What records can users see?">
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        { v: 'all', label: 'View All Records', desc: 'Users can see every record in this worklist regardless of assignment', icon: '🌐' },
                                        { v: 'assigned', label: 'Assigned Only', desc: 'Users can only see records that are assigned to them', icon: '🎯' },
                                    ].map(opt => (
                                        <button key={opt.v} type="button" onClick={() => patch('record_visibility', opt.v)}
                                            className={`text-left p-4 rounded-2xl border-2 transition-all ${
                                                form.record_visibility === opt.v
                                                    ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20'
                                                    : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 bg-white dark:bg-slate-900'
                                            }`}>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className="text-base">{opt.icon}</span>
                                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.record_visibility === opt.v ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300 dark:border-slate-600'}`}>
                                                    {form.record_visibility === opt.v && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                                </div>
                                            </div>
                                            <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{opt.label}</p>
                                            <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 leading-relaxed">{opt.desc}</p>
                                        </button>
                                    ))}
                                </div>
                            </Field>
                        </div>

                        {/* ── SECTION: Assignee Configuration ── */}
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                <div className="w-5 h-5 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                                    <span className="text-[10px]">👤</span>
                                </div>
                                <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Assignee Configuration</p>
                            </div>

                            <UserPillSelect
                                label="Assignee Pool"
                                hint="Only users in this pool can be assigned work items. Leave empty to allow any user with access."
                                users={users}
                                selectedIds={form.assignee_pool || []}
                                onAdd={id => patch('assignee_pool', [...(form.assignee_pool || []), id])}
                                onRemove={id => patch('assignee_pool', (form.assignee_pool || []).filter(x => x !== id))}
                                searchVal={userSearchAssignee}
                                onSearch={setUserSearchAssignee}
                            />

                            <Field label="Default Assignee">
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-2">New items will be auto-assigned to this user. Select None to leave unassigned.</p>
                                <select className={input} value={form.default_assignee || ''} onChange={e => patch('default_assignee', e.target.value)}>
                                    <option value="">None (Unassigned)</option>
                                    {(form.assignee_pool?.length > 0
                                        ? users.filter(u => form.assignee_pool.includes(u.id))
                                        : users
                                    ).map(u => (
                                        <option key={u.id} value={u.id}>{u.name || u.email}</option>
                                    ))}
                                </select>
                            </Field>
                        </div>


                        {/* ── SECTION: Locked Statuses ── */}
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                <div className="w-5 h-5 rounded-lg bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                                    <span className="text-[10px]">🔒</span>
                                </div>
                                <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Locked Statuses</p>
                            </div>
                            <Field label="Record Lock">
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-3">Items in these statuses cannot be edited by regular users. Admins can still override.</p>
                                <div className="flex flex-wrap gap-2">
                                    {(form.workflow_config?.statuses?.length > 0
                                        ? form.workflow_config.statuses
                                        : ['Open', 'In Progress', 'Pending Review', 'Closed']
                                    ).map(s => {
                                        const terminalStatuses = form.workflow_config?.terminal_statuses || ['Closed'];
                                        const isLocked = terminalStatuses.includes(s);
                                        return (
                                            <button key={s} type="button"
                                                onClick={() => {
                                                    const current = form.workflow_config?.terminal_statuses || ['Closed'];
                                                    const next = isLocked ? current.filter(x => x !== s) : [...current, s];
                                                    patch('workflow_config', { ...(form.workflow_config || {}), terminal_statuses: next });
                                                }}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-all ${
                                                    isLocked
                                                        ? 'border-red-400 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400'
                                                        : 'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:border-red-300 dark:hover:border-red-800 bg-white dark:bg-slate-900'
                                                }`}>
                                                {isLocked ? '🔒' : '🔓'} {s}
                                            </button>
                                        );
                                    })}
                                </div>
                                {(form.workflow_config?.terminal_statuses?.length > 0) && (
                                    <p className="mt-2 text-[10px] text-red-500 dark:text-red-400 font-semibold">
                                        🔒 {form.workflow_config.terminal_statuses.join(', ')} — locked for regular users
                                    </p>
                                )}
                                {(!form.schema_map?.some(r => r.core_mapping === 'Status') && !form.schema_map?.some(r => r.is_status_column)) && (
                                    <p className="mt-1 text-[10px] text-amber-500 font-semibold">⚠ No Status column mapped in Schema — configure statuses in Step 2 first</p>
                                )}
                            </Field>
                        </div>

                        {/* ── SECTION: Default View ── */}
                        <div className="space-y-4 pt-2">
                            <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-slate-800">
                                <div className="w-5 h-5 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                                    <span className="text-[10px]">🔀</span>
                                </div>
                                <p className="text-xs font-bold text-gray-700 dark:text-slate-200 uppercase tracking-wide">Default View</p>
                                <p className="text-[10px] text-gray-400 dark:text-slate-500 ml-auto">Applied when users open this worklist</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Default Sort Column">
                                    <select
                                        value={form.default_sort_col || ''}
                                        onChange={e => patch('default_sort_col', e.target.value)}
                                        className={input}>
                                        <option value="">— None (server order) —</option>
                                        {(form.schema_map || []).filter(r => r.is_selected !== false && r.source_field && !r.source_field.endsWith(' [Removed]')).map(r => (
                                            <option key={r.source_field} value={r.source_field}>{r.source_field}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Default Sort Direction">
                                    <select
                                        value={form.default_sort_dir || 'asc'}
                                        onChange={e => patch('default_sort_dir', e.target.value)}
                                        disabled={!form.default_sort_col}
                                        className={`${input} disabled:opacity-40`}>
                                        <option value="asc">↑ Ascending (A → Z, 0 → 9)</option>
                                        <option value="desc">↓ Descending (Z → A, 9 → 0)</option>
                                    </select>
                                </Field>
                            </div>
                            {form.default_sort_col && (
                                <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-semibold">
                                    ✓ Worklist opens sorted by <strong>{form.default_sort_col}</strong> {form.default_sort_dir === 'desc' ? 'descending ↓' : 'ascending ↑'} by default
                                </p>
                            )}
                        </div>

                    </StepCard>
                )}

                {/* ══ STEP 4 — WRITE-BACK ══════════════════════════════════════════ */}
                {step === 4 && (() => {
                    const editableFields = (form.schema_map || []).filter(r => r.is_selected !== false && !r.is_read_only);
                    const sink = form.writeback_sink || { type: 'same_as_source', write_mode: 'update_rows', target_type: 'table' };
                    const patchSink = (key, val) => setForm(f => ({ ...f, writeback_sink: { ...(f.writeback_sink || {}), [key]: val } }));

                    let sourceSummary = '';
                    if (form.source_type === 'sharepoint') {
                        const sp = form.sharepoint_config || {};
                        sourceSummary = `${sp.workbook_path || sp.workbook_file || 'workbook'} → ${sp.worksheet_name || 'Sheet1'}`;
                    } else if (form.source_type === 'data_model') {
                        const dm = dataModels.find(d => d.id === form.data_model_id);
                        sourceSummary = `${dm?.name || 'Data Model'} / ${form.data_model_table || 'table'} (underlying SQL)`;
                    } else {
                        sourceSummary = form.source_table || 'SQL source';
                    }

                    const sinkTypes = [
                        { id: 'same_as_source', icon: '↩️', label: 'Same as Source',      desc: sourceSummary },
                        { id: 'sql',            icon: '🗄️', label: 'Different SQL Table',  desc: 'Write to any SQL datasource + table' },
                        { id: 'sharepoint',     icon: '📋', label: 'Different SharePoint', desc: 'Write to another SharePoint workbook / sheet' },
                        { id: 'flow',           icon: '⚡', label: 'Trigger a Flow',        desc: 'POST changes to an Aidas Flow for processing' },
                        { id: 'platform',       icon: '🔒', label: 'Platform-managed only', desc: 'Keep edits inside the platform — no external push' },
                    ];

                    const triggers = [
                        { id: 'realtime',   Icon: Zap,        label: 'Real-time',     desc: 'Saves and syncs immediately on every cell edit' },
                        { id: 'bulk_admin', Icon: Shield,      label: 'Bulk — Admins', desc: 'Changes accumulate; only Admins trigger Sync' },
                        { id: 'bulk_all',   Icon: Users,       label: 'Bulk — All',    desc: 'Any user can trigger Sync Changes' },
                        { id: 'scheduled',  Icon: Clock,       label: 'Scheduled',     desc: 'Auto-sync on a cron schedule' },
                    ];

                    return (
                        <StepCard title="Write-back" desc="Define WHERE edits go, WHAT fields are synced, and WHEN the sync happens.">

                            {/* ── A: Sink Target ──────────────────────────────────────── */}
                            <div>
                                <p className="text-xs font-extrabold text-gray-700 dark:text-slate-300 mb-3">
                                    A — Write-back Target <span className="font-normal text-gray-400">(WHERE edits go)</span>
                                </p>
                                <div className="grid grid-cols-1 gap-2">
                                    {sinkTypes.map(s => (
                                        <button key={s.id} type="button" onClick={() => patchSink('type', s.id)}
                                            className={`text-left px-4 py-3 rounded-2xl border-2 transition-all flex items-center gap-3 ${
                                                sink.type === s.id
                                                    ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20'
                                                    : 'border-gray-200 dark:border-slate-700 hover:border-indigo-300 bg-white dark:bg-slate-900'
                                            }`}>
                                            <span className="text-xl shrink-0">{s.icon}</span>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{s.label}</p>
                                                <p className="text-[10px] text-gray-400 dark:text-slate-500 truncate">{s.desc}</p>
                                            </div>
                                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                sink.type === s.id ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300 dark:border-slate-600'
                                            }`}>
                                                {sink.type === s.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                {/* SharePoint same-as-source write mode */}
                                {sink.type === 'same_as_source' && form.source_type === 'sharepoint' && (
                                    <div className="mt-3 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-2xl space-y-3">
                                        <p className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-widest">SharePoint Write Mode</p>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { v: 'update_rows', icon: '✏️', label: 'Update Rows',  desc: 'PATCH rows by PK' },
                                                { v: 'append',      icon: '➕', label: 'Append Rows',  desc: 'Add to end of table' },
                                                { v: 'replace',     icon: '🔄', label: 'Replace All',  desc: 'Clear + rewrite all' },
                                            ].map(wm => (
                                                <button key={wm.v} type="button" onClick={() => patchSink('write_mode', wm.v)}
                                                    className={`text-left p-3 rounded-xl border-2 transition-all ${
                                                        sink.write_mode === wm.v ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-indigo-300'
                                                    }`}>
                                                    <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{wm.icon} {wm.label}</p>
                                                    <p className="text-[10px] text-gray-400 mt-0.5">{wm.desc}</p>
                                                </button>
                                            ))}
                                        </div>
                                        {sink.write_mode === 'update_rows' && (
                                            <p className="text-[10px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-xl font-semibold">
                                                ℹ️ Matches rows by the <strong>Primary Key</strong> column from Step 2. Only editable fields are PATCH'd. The rest of the sheet is untouched.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* SQL sink sub-config */}
                                {sink.type === 'sql' && (
                                    <div className="mt-3 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-2xl space-y-3">
                                        <p className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-widest">SQL Target</p>
                                        <Field label="Target Datasource">
                                            <select className={input} value={sink.datasource_id || ''} onChange={e => patchSink('datasource_id', e.target.value)}>
                                                <option value="">— select datasource —</option>
                                                {datasources.map(ds => <option key={ds.id} value={ds.id}>{ds.name}</option>)}
                                            </select>
                                        </Field>
                                        <Field label="Target Table">
                                            <input className={input} placeholder="e.g. dbo.WorklistResults" value={sink.table || ''} onChange={e => patchSink('table', e.target.value)} />
                                        </Field>
                                        <Field label="Write Mode">
                                            <div className="grid grid-cols-2 gap-2">
                                                {[
                                                    { v: 'update',  label: 'Update',  desc: 'UPDATE by PK — existing rows only' },
                                                    { v: 'upsert',  label: 'Upsert',  desc: 'Insert or Update (safe merge)' },
                                                    { v: 'insert',  label: 'Insert',  desc: 'Insert new rows only' },
                                                    { v: 'replace', label: 'Replace', desc: 'Truncate + Insert all rows' },
                                                ].map(wm => (
                                                    <button key={wm.v} type="button" onClick={() => patchSink('write_mode', wm.v)}
                                                        className={`text-left p-3 rounded-xl border-2 transition-all ${
                                                            sink.write_mode === wm.v ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-indigo-300'
                                                        }`}>
                                                        <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{wm.label}</p>
                                                        <p className="text-[10px] text-gray-400">{wm.desc}</p>
                                                    </button>
                                                ))}
                                            </div>
                                        </Field>
                                    </div>
                                )}

                                {/* SharePoint different sink sub-config */}
                                {sink.type === 'sharepoint' && (
                                    <div className="mt-3 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-2xl space-y-3">
                                        <p className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-widest">Target SharePoint</p>
                                        {[['site_hostname','Domain','tenant.sharepoint.com'],['site_path','Site Path','/sites/mysite'],['workbook_path','Workbook Path','Reports/data.xlsx'],['worksheet_name','Sheet','Sheet1']].map(([k,lbl,ph]) => (
                                            <Field key={k} label={lbl}>
                                                <input className={input} placeholder={ph} value={sink[k] || ''} onChange={e => patchSink(k, e.target.value)} />
                                            </Field>
                                        ))}
                                        <Field label="Target Type">
                                            <div className="grid grid-cols-2 gap-2">
                                                {[
                                                    { v: 'table', label: '📋 Named Table', desc: 'Row PATCH or append to an Excel Table' },
                                                    { v: 'range', label: '📐 Range',        desc: 'Append or replace an address range' },
                                                ].map(tt => (
                                                    <button key={tt.v} type="button" onClick={() => patchSink('target_type', tt.v)}
                                                        className={`text-left p-3 rounded-xl border-2 transition-all ${
                                                            (sink.target_type || 'table') === tt.v ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-indigo-300'
                                                        }`}>
                                                        <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{tt.label}</p>
                                                        <p className="text-[10px] text-gray-400">{tt.desc}</p>
                                                    </button>
                                                ))}
                                            </div>
                                        </Field>
                                        {sink.target_type === 'table' && (
                                            <Field label="Table Name"><input className={input} placeholder="Table1" value={sink.table_name || ''} onChange={e => patchSink('table_name', e.target.value)} /></Field>
                                        )}
                                        {sink.target_type === 'range' && (
                                            <Field label="Range Address"><input className={input} placeholder="A1:H500" value={sink.range_address || ''} onChange={e => patchSink('range_address', e.target.value)} /></Field>
                                        )}
                                        <Field label="Write Mode">
                                            <div className="grid grid-cols-1 gap-2">
                                                {((sink.target_type || 'table') === 'table'
                                                    ? [{ v: 'update_rows', label: '✏️ Update Rows', desc: 'PATCH individual rows by Primary Key' }, { v: 'append', label: '➕ Append', desc: 'Add rows to end of table' }]
                                                    : [{ v: 'append', label: '➕ Append', desc: 'Write below last data row in range' }, { v: 'replace', label: '🔄 Replace', desc: 'Clear range then write all rows' }]
                                                ).map(wm => (
                                                    <button key={wm.v} type="button" onClick={() => patchSink('write_mode', wm.v)}
                                                        className={`text-left p-3 rounded-xl border-2 transition-all ${
                                                            sink.write_mode === wm.v ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-indigo-300'
                                                        }`}>
                                                        <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{wm.label}</p>
                                                        <p className="text-[10px] text-gray-400">{wm.desc}</p>
                                                    </button>
                                                ))}
                                            </div>
                                        </Field>
                                    </div>
                                )}

                                {/* Flow sink sub-config */}
                                {sink.type === 'flow' && (
                                    <div className="mt-3 p-4 bg-gray-50 dark:bg-slate-800/50 rounded-2xl space-y-3">
                                        <p className="text-[10px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-widest">Flow Target</p>
                                        <Field label="Target Flow">
                                            <select className={input} value={sink.flow_id || ''} onChange={e => patchSink('flow_id', e.target.value)}>
                                                <option value="">— select a flow —</option>
                                                {flows.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                            </select>
                                        </Field>
                                        <Field label="Payload Mode">
                                            <div className="grid grid-cols-2 gap-2">
                                                {[
                                                    { v: 'delta', label: '🔺 Delta only', desc: 'Changed fields + PK only' },
                                                    { v: 'full',  label: '📦 Full record', desc: 'Complete resolved row' },
                                                ].map(pm => (
                                                    <button key={pm.v} type="button" onClick={() => patchSink('payload_mode', pm.v)}
                                                        className={`text-left p-3 rounded-xl border-2 transition-all ${
                                                            (sink.payload_mode || 'delta') === pm.v ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-indigo-300'
                                                        }`}>
                                                        <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{pm.label}</p>
                                                        <p className="text-[10px] text-gray-400">{pm.desc}</p>
                                                    </button>
                                                ))}
                                            </div>
                                        </Field>
                                    </div>
                                )}

                                {/* Platform-only info */}
                                {sink.type === 'platform' && (
                                    <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-800/40">
                                        <p className="text-[11px] text-blue-700 dark:text-blue-300 font-semibold">
                                            🔒 <strong>Platform-managed only</strong> — user edits are stored inside the platform only. No data is pushed to any external system. The "Sync Changes" button will be hidden from users.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* ── B: Fields to Write Back ──────────────────────────────── */}
                            {sink.type !== 'platform' && (
                                <div className="pt-5 border-t border-gray-100 dark:border-slate-800">
                                    <p className="text-xs font-extrabold text-gray-700 dark:text-slate-300 mb-1">
                                        B — Fields to Write Back <span className="font-normal text-gray-400">(WHAT is synced)</span>
                                    </p>
                                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-3">Read-only fields are always excluded. Un-tick to exclude an editable field from sync.</p>
                                    {editableFields.length === 0 ? (
                                        <p className="text-[11px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-xl">⚠️ No editable fields — go back to Step 2 and mark some fields as Editable.</p>
                                    ) : (
                                        <div className="space-y-1">
                                            {editableFields.map(r => {
                                                const allNames = editableFields.map(f => f.source_field);
                                                const included = !form.writeback_fields || form.writeback_fields.includes(r.source_field);
                                                return (
                                                    <label key={r.source_field} className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors">
                                                        <input type="checkbox" checked={included} className="w-4 h-4 rounded accent-indigo-600"
                                                            onChange={e => {
                                                                const base = form.writeback_fields || allNames;
                                                                const next = e.target.checked
                                                                    ? [...new Set([...base, r.source_field])]
                                                                    : base.filter(f => f !== r.source_field);
                                                                setForm(f => ({ ...f, writeback_fields: next }));
                                                            }} />
                                                        <div>
                                                            <p className="text-xs font-semibold text-gray-800 dark:text-slate-200">{r.source_field}</p>
                                                            <p className="text-[10px] text-gray-400">{r.field_type || 'Text Box'}{r.core_mapping && r.core_mapping !== 'None' ? ` · ${r.core_mapping}` : ''}</p>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── C: Sync Trigger ──────────────────────────────────────── */}
                            <div className="pt-5 border-t border-gray-100 dark:border-slate-800">
                                <p className="text-xs font-extrabold text-gray-700 dark:text-slate-300 mb-3">
                                    C — Sync Trigger <span className="font-normal text-gray-400">(WHEN it happens)</span>
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    {triggers.map(t => {
                                        const TIcon = t.Icon;
                                        const active = form.writeback_trigger === t.id;
                                        return (
                                            <button key={t.id} type="button" onClick={() => setForm(f => ({ ...f, writeback_trigger: t.id }))}
                                                className={`text-left p-3 rounded-2xl border-2 transition-all ${
                                                    active ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-slate-700 hover:border-indigo-300 bg-white dark:bg-slate-900'
                                                }`}>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <TIcon className={`w-3.5 h-3.5 ${active ? 'text-indigo-500' : 'text-gray-400'}`} />
                                                    <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{t.label}</p>
                                                </div>
                                                <p className="text-[10px] text-gray-400 dark:text-slate-500">{t.desc}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                                {form.writeback_trigger === 'scheduled' && (
                                    <div className="mt-3">
                                        <Field label="Cron Schedule">
                                            <input className={input} placeholder="0 * * * *  (every hour)"
                                                value={form.writeback_schedule || ''}
                                                onChange={e => setForm(f => ({ ...f, writeback_schedule: e.target.value }))} />
                                            <p className="text-[10px] text-gray-400 mt-1">Standard 5-field cron. Example: <code>0 8 * * 1-5</code> = weekdays at 8 AM</p>
                                        </Field>
                                    </div>
                                )}
                            </div>

                        </StepCard>
                    );
                })()}

                {/* ══ STEP 5 — PUBLISH ════════════════════════════════════════════ */}
                {step === 5 && (
                    <StepCard title="Publish" desc="Review your configuration, then publish this worklist to make it available to users.">

                        {/* ── Config summary ── */}

                        {(() => {
                            // is_selected defaults to true — never exclude a row unless explicitly deselected
                            const allCols  = form.schema_map || [];
                            const selectedCols = Array.isArray(allCols)
                                ? allCols.filter(r => r.is_selected !== false)
                                : [];
                            return (
                            <div className="space-y-3 bg-gray-50 dark:bg-slate-800/50 rounded-2xl p-4">
                                <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-1">Configuration Summary</p>
                                <Row label="Name"       value={form.name} />
                                <Row label="Source"     value={
                                    form.source_type === 'data_model'
                                         ? `Data Model: ${dataModels.find(d => d.id === form.data_model_id)?.name || form.data_model_id} / ${form.data_model_table}`
                                         : form.source_type === 'sql' ? `SQL — ${form.source_table || 'Custom query'}` : 'SharePoint'
                                } />
                                <Row label="Statuses"   value={(form.workflow_config?.statuses || []).join(' → ') || '—'} />
                                <Row label="Access"     value={
                                    !form.shared_with || form.shared_with.includes('*')
                                        ? 'All users'
                                        : form.shared_with.map(id => roles.find(r => r.id === id)?.name || id).join(', ')
                                } />
                                <Row label="Visibility"  value={form.record_visibility === 'assigned' ? 'Assigned items only' : 'All records'} />
                                <Row label="Write-back Target" value={
                                    form.writeback_sink?.type === 'same_as_source' ? `Same as source (${form.source_type})` :
                                    form.writeback_sink?.type === 'sql'            ? `SQL → ${form.writeback_sink.table || '(table not set)'}` :
                                    form.writeback_sink?.type === 'sharepoint'     ? `SharePoint → ${form.writeback_sink.workbook_path || '(not set)'}` :
                                    form.writeback_sink?.type === 'flow'           ? `Flow → ${flows.find(f => f.id === form.writeback_sink?.flow_id)?.name || '(not set)'}` :
                                    form.writeback_sink?.type === 'platform'       ? 'Platform-managed only' : '—'
                                } />
                                <Row label="Write Mode" value={form.writeback_sink?.write_mode || 'update_rows'} />
                                <Row label="Sync Trigger" value={
                                    form.writeback_trigger === 'realtime'   ? 'Real-time (instant)' :
                                    form.writeback_trigger === 'bulk_admin' ? 'Bulk — Admins only' :
                                    form.writeback_trigger === 'bulk_all'   ? 'Bulk — All users' :
                                    form.writeback_trigger === 'scheduled'  ? `Scheduled: ${form.writeback_schedule || '(cron not set)'}` :
                                    (form.writeback_trigger || '—')
                                } />

                                {/* Schema — flat list of every selected column, exactly as configured in Step 2 */}
                                <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
                                    <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-2">
                                        Schema — {selectedCols.length} of {allCols.length} columns included
                                    </p>
                                    {selectedCols.length === 0
                                        ? <p className="text-[11px] text-gray-400">No columns selected — go back to Step 2</p>
                                        : (
                                        <table className="w-full text-xs border-collapse">
                                            <thead>
                                                <tr className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wide">
                                                    <th className="text-left pb-1.5 font-semibold w-[35%]">Field</th>
                                                    <th className="text-left pb-1.5 font-semibold w-[25%]">Core Mapping</th>
                                                    <th className="text-left pb-1.5 font-semibold w-[20%]">Field Type</th>
                                                    <th className="text-left pb-1.5 font-semibold w-[20%]">Access</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedCols.map((r, i) => (
                                                    <tr key={r.source_field || i} className={i % 2 === 0 ? 'bg-white/60 dark:bg-slate-800/40' : ''}>
                                                        <td className="py-1 pr-2 font-semibold text-gray-800 dark:text-slate-200">{r.source_field}</td>
                                                        <td className="py-1 pr-2 text-gray-500 dark:text-slate-400">{r.core_mapping || '—'}</td>
                                                        <td className="py-1 pr-2 text-gray-500 dark:text-slate-400">{r.field_type || 'As-Is'}</td>
                                                        <td className="py-1">
                                                            {r.is_read_only
                                                                ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-[10px] font-bold">🔒 Read-Only</span>
                                                                : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded text-[10px] font-bold">✏ Editable</span>
                                                            }
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                            );
                        })()}

                        {/* ── Folder ── */}
                        <Field label="Folder (optional)">
                            <input className={input} placeholder="e.g. Finance / RCM" value={form.folder_name} onChange={e => patch('folder_name', e.target.value)} />
                            <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">Group this worklist under a folder for easier navigation in the sidebar.</p>
                        </Field>

                        {/* ── Publish scope ── */}
                        <Field label="Publish Scope">
                            <div className="grid grid-cols-1 gap-3">
                                {[
                                    { v: 'draft',    icon: '📝', label: 'Save as Draft',     desc: 'Save configuration but do not expose to users yet. Only admins can see it.',      badge: 'Private',   badgeColor: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300' },
                                    { v: 'internal', icon: '🏢', label: 'Publish Internally', desc: 'Visible to the roles and users configured in the Access step.',                   badge: 'Scoped',    badgeColor: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
                                    { v: 'public',   icon: '🌐', label: 'Publish to All',     desc: 'Override the access configuration and make this worklist visible to every user.', badge: 'Open',      badgeColor: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
                                ].map(opt => (
                                    <button key={opt.v} type="button" onClick={() => patch('publish_scope', opt.v)}
                                        className={`text-left p-4 rounded-2xl border-2 transition-all ${
                                            (form.publish_scope || 'internal') === opt.v
                                                ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 bg-white dark:bg-slate-900'
                                        }`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-2.5">
                                                <span className="text-xl">{opt.icon}</span>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-xs font-bold text-gray-800 dark:text-slate-200">{opt.label}</p>
                                                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${opt.badgeColor}`}>{opt.badge}</span>
                                                    </div>
                                                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5 leading-relaxed">{opt.desc}</p>
                                                </div>
                                            </div>
                                            <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                (form.publish_scope || 'internal') === opt.v ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300 dark:border-slate-600'
                                            }`}>
                                                {(form.publish_scope || 'internal') === opt.v && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </Field>

                        {/* ── Preview CTA ── */}
                        {savedId && (
                            <div className="p-4 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/30 dark:to-violet-950/30 rounded-2xl border border-indigo-100 dark:border-indigo-900/40">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-bold text-indigo-700 dark:text-indigo-300">Preview Ready</p>
                                        <p className="text-[10px] text-indigo-500 dark:text-indigo-400 mt-0.5">See exactly how this worklist will appear to your users before publishing.</p>
                                    </div>
                                    <button type="button" onClick={() => setShowPreview(true)}
                                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm">
                                        <Eye className="w-3.5 h-3.5" /> Preview
                                    </button>
                                </div>
                            </div>
                        )}
                        {!savedId && (
                            <p className="text-[10px] text-gray-400 dark:text-slate-500 text-center">Save a draft first to unlock the Preview option.</p>
                        )}

                    </StepCard>
                )}

            </div>

            {/* Footer nav */}
            <div className="flex-none px-6 py-4 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button disabled={step === 1} onClick={() => { setErrors({}); setStep(s => s - 1); }}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    {step < 5 && (
                        <button onClick={async () => {
                            if (!validateStep(1)) {
                                addNotification('Worklist Name and Source are required to save a draft', 'warning');
                                return;
                            }
                            setSaving(true);
                            try {
                                const payload = { ...form };
                                if (payload.source_type === 'sharepoint' && payload.writeback_mode === 'sql_table') {
                                    payload.writeback_mode = 'platform_managed';
                                }
                                if (isEdit) {
                                    await apiPatch(`/worklists/${appId}`, token, payload);
                                    addNotification('Draft saved successfully', 'success');
                                } else {
                                    const res = await apiPost('/worklists', token, payload);
                                    addNotification('Draft created successfully', 'success');
                                    setSavedId(res.id);
                                    if (onDraftSaved) onDraftSaved(res.id);
                                }
                            } catch(e) {
                                addNotification(`Save failed: ${e?.message || 'Unknown error'}`, 'error');
                            } finally {
                                setSaving(false);
                            }
                        }} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-indigo-500" />} Save Draft
                        </button>
                    )}
                </div>
                {step < 5 ? (
                    <button onClick={handleNext}
                        className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold shadow-sm transition-all">
                        Next <ArrowRight className="w-4 h-4" />
                    </button>
                ) : (
                    <div className="flex items-center gap-3">
                        {savedId && (
                            <button onClick={() => setShowPreview(true)}
                                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-300 dark:border-indigo-700 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-all">
                                <Eye className="w-4 h-4" /> Preview
                            </button>
                        )}
                        <button onClick={() => setShowConfirm(true)} disabled={saving}
                            className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-md transition-all disabled:opacity-60">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                            {isEdit ? 'Save & Publish' : 'Create & Publish'}
                        </button>
                    </div>
                )}
            </div>
        </div>

        {/* ── Preview Modal ──────────────────────────────────────────── */}
        {showPreview && savedId && (
            <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-950">
                {/* Modal header */}
                <div className="flex-none flex items-center justify-between px-6 py-3 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 shadow-sm">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setShowPreview(false)}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-slate-700 text-xs font-semibold text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
                            <ArrowLeft className="w-3.5 h-3.5" /> Back to Editor
                        </button>
                        <div className="h-4 w-px bg-gray-200 dark:bg-slate-700" />
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full text-[11px] font-bold">
                            <Eye className="w-3 h-3" /> PREVIEW MODE
                        </span>
                        <p className="text-xs text-gray-400 dark:text-slate-500">This is exactly what your users will see</p>
                    </div>
                    <button
                        onClick={async () => {
                            setSaving(true);
                            try {
                                const payload = { ...form, status: 'published' };
                                if (isEdit) {
                                    await apiPatch(`/worklists/${savedId}`, token, payload);
                                } else {
                                    await apiPatch(`/worklists/${savedId}`, token, payload);
                                }
                                addNotification('Worklist published successfully 🎉', 'success');
                                setShowPreview(false);
                                if (onSaved) onSaved(savedId);
                            } catch(e) {
                                addNotification(`Publish failed: ${e?.message || 'Unknown error'}`, 'error');
                            } finally { setSaving(false); }
                        }}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-md transition-all disabled:opacity-60">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                        Confirm &amp; Publish
                    </button>
                </div>
                {/* Runner embedded full height */}
                <div className="flex-1 overflow-hidden">
                    <Suspense fallback={
                        <div className="flex items-center justify-center h-full gap-2">
                            <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                            <span className="text-sm text-gray-400">Loading preview…</span>
                        </div>
                    }>
                        <WorklistRunnerPage appId={savedId} onBack={() => setShowPreview(false)} isPreview={true} />
                    </Suspense>
                </div>
            </div>
        )}
        </>
    );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const input = 'w-full px-3.5 py-2.5 text-sm bg-gray-50/50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/80 rounded-xl outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:text-slate-100 transition-all duration-200';

function StepCard({ title, desc, children }) {
    return (
        <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md rounded-2xl border border-gray-100 dark:border-slate-800/80 p-6 space-y-5 shadow-[0_8px_30px_rgb(0,0,0,0.02)]">
            <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-slate-100">{title}</h2>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{desc}</p>
            </div>
            {children}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-gray-600 dark:text-slate-400 uppercase tracking-wider">{label}</label>
            {children}
        </div>
    );
}

function ColSelect({ cols, value, onChange }) {
    return (
        <select className={input} value={value} onChange={e => onChange(e.target.value)}>
            <option value="">— Not mapped —</option>
            {cols.map(c => <option key={c} value={c}>{c}</option>)}
            {!cols.includes(value) && value && <option value={value}>{value} (manual)</option>}
        </select>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-start gap-3">
            <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 w-24 shrink-0">{label}</span>
            <span className="text-sm text-gray-900 dark:text-slate-100">{value || '—'}</span>
        </div>
    );
}
